import { useState, useEffect } from "react";
import {
  Plus,
  Minus,
  Maximize,
  X,
  Grid,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Monitor,
  Eye,
} from "lucide-react";
import closeScreen from "../assets/images/closeScreen.svg";
import incressText from "../assets/images/incressText.svg";
import decrssText from "../assets/images/decrssText.svg";
import { useTranslation } from "react-i18next";

const ControlBar = ({
  selectedVerse,
  screens = [],
  setScreens = () => {},
  isEndOfSong,
}) => {
  const [externalWindows, setExternalWindows] = useState({});
  const [blackScreens, setBlackScreens] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState("AR");
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const languages = ["AR", "EN", "ARZ", "ES"];
  const languageFullNames = {
    AR: "arabic",
    EN: "english",
    ARZ: "franco",
    ES: "spanish",
  };

  // Update external window content
  const updateExternalWindow = (
    window,
    verse,
    displayOptions,
    fontSizes,
    isBlackScreen
  ) => {
    const shouldShowBlackScreen = isBlackScreen || isEndOfSong;

    if (shouldShowBlackScreen) {
      window.document.querySelector(".content").innerHTML = `
        <div style="width: 100vw; height: 100vh; background: black; position: fixed; top: 0; left: 0; display: flex; justify-content: center; align-items: center;">
          <img src="https://res.cloudinary.com/dtcr7vypb/image/upload/v1736086718/cee93lnyiospefvpmwvp.svg" alt="placeholder" style="max-width: 80%; max-height: 80%; object-fit: contain;" />
        </div>
      `;
      return;
    }

    if (!verse) {
      window.document.querySelector(".content").innerHTML = `
        <div style="width: 100vw; height: 100vh; background: black; position: fixed; top: 0; left: 0;"></div>
      `;
      return;
    }

    const safeFontSizes = fontSizes || {
      arabic: 4.0,
      english: 3.5,
      franco: 3.0,
      spanish: 3.0,
    };

    const activeLanguages =
      Object.values(displayOptions).filter(Boolean).length;
    let fontSize;
    if (activeLanguages === 1) {
      fontSize = `min(${safeFontSizes.arabic * 3.0}rem, 12vw)`;
    } else if (activeLanguages === 2) {
      fontSize = `min(${safeFontSizes.arabic * 2.0}rem, 8vw)`;
    } else {
      fontSize = `min(${safeFontSizes.arabic * 1.5}rem, 6vw)`;
    }

    const content = `
      <div class="content" style="width: 100vw; min-height: 100vh; display: flex; flex-direction: column; justify-content: flex-start; align-items: center; padding: 2rem 0; margin: 0; position: relative; overflow-y: auto; gap: ${
        activeLanguages > 1 ? "2rem" : "1rem"
      }">
        ${
          displayOptions.arabic
            ? `<div class="verse arabic" style="font-size: ${fontSize}; width: 95%; max-width: 1400px; display: flex; justify-content: center; align-items: center; text-align: center; padding: ${
                activeLanguages > 1 ? "1rem" : "2rem"
              }; word-wrap: break-word; line-height: 1.4;">${
                verse.arabic || ""
              }</div>`
            : ""
        }
        ${
          displayOptions.english
            ? `<div class="verse english" style="font-size: ${fontSize}; width: 95%; max-width: 1400px; display: flex; justify-content: center; align-items: center; text-align: center; padding: ${
                activeLanguages > 1 ? "1rem" : "2rem"
              }; word-wrap: break-word; line-height: 1.4;">${
                verse.english || ""
              }</div>`
            : ""
        }
        ${
          displayOptions.franco
            ? `<div class="verse franco" style="font-size: ${fontSize}; width: 95%; max-width: 1400px; display: flex; justify-content: center; align-items: center; text-align: center; padding: ${
                activeLanguages > 1 ? "1rem" : "2rem"
              }; word-wrap: break-word; line-height: 1.4;">${
                verse.franco || ""
              }</div>`
            : ""
        }
        ${
          displayOptions.spanish
            ? `<div class="verse spanish" style="font-size: ${fontSize}; width: 95%; max-width: 1400px; display: flex; justify-content: center; align-items: center; text-align: center; padding: ${
                activeLanguages > 1 ? "1rem" : "2rem"
              }; word-wrap: break-word; line-height: 1.4;">${
                verse.spanish || ""
              }</div>`
            : ""
        }
      </div>
    `;

    window.document.querySelector(".content").innerHTML = content;
  };

  const toggleLanguage = (lang) => {
    const langName = languageFullNames[lang];
    setScreens((prev) =>
      prev.map((screen) => ({
        ...screen,
        displayOptions: {
          ...screen.displayOptions,
          [langName]: !screen.displayOptions[langName],
        },
      }))
    );
    setSelectedLanguage(lang);
  };

  const adjustFontSize = (screenId, increase) => {
    setScreens((prev) =>
      prev.map((screen) => {
        if (screen.id === screenId) {
          const updatedScreen = {
            ...screen,
            fontSizes: {
              arabic: Math.max(
                0.8,
                Math.min(
                  40,
                  (screen.fontSizes?.arabic || 4.0) + (increase ? 0.5 : -0.5)
                )
              ),
              english: Math.max(
                0.8,
                Math.min(
                  40,
                  (screen.fontSizes?.english || 3.5) + (increase ? 0.5 : -0.5)
                )
              ),
              franco: Math.max(
                0.8,
                Math.min(
                  40,
                  (screen.fontSizes?.franco || 3.0) + (increase ? 0.5 : -0.5)
                )
              ),
              spanish: Math.max(
                0.8,
                Math.min(
                  40,
                  (screen.fontSizes?.spanish || 3.0) + (increase ? 0.5 : -0.5)
                )
              ),
            },
          };
          const externalWindow = externalWindows[screenId];
          if (externalWindow && !externalWindow.closed && selectedVerse) {
            updateExternalWindow(
              externalWindow,
              selectedVerse,
              updatedScreen.displayOptions,
              updatedScreen.fontSizes,
              blackScreens[screenId]
            );
          }
          return updatedScreen;
        }
        return screen;
      })
    );
  };

  const resetFontSizes = (screenId) => {
    setScreens((prev) =>
      prev.map((screen) => {
        if (screen.id === screenId) {
          const defaultFontSizes = {
            arabic: 4.0,
            english: 3.5,
            franco: 3.0,
            spanish: 3.0,
          };
          const updatedScreen = {
            ...screen,
            fontSizes: defaultFontSizes,
          };
          const externalWindow = externalWindows[screenId];
          if (externalWindow && !externalWindow.closed) {
            updateExternalWindow(
              externalWindow,
              selectedVerse,
              updatedScreen.displayOptions,
              defaultFontSizes,
              blackScreens[screenId]
            );
          }
          return updatedScreen;
        }
        return screen;
      })
    );
  };

  const toggleBlackScreen = (screenId) => {
    setBlackScreens((prev) => {
      const newBlackScreens = {
        ...prev,
        [screenId]: !prev[screenId],
      };
      const externalWindow = externalWindows[screenId];
      if (externalWindow && !externalWindow.closed) {
        updateExternalWindow(
          externalWindow,
          selectedVerse,
          screens.find((s) => s.id === screenId).displayOptions,
          screens.find((s) => s.id === screenId).fontSizes,
          newBlackScreens[screenId]
        );
      }
      return newBlackScreens;
    });
  };

  const addScreen = () => {
    const newId = screens.length + 1;
    setScreens((prev) => [
      ...prev,
      {
        id: newId,
        displayOptions: {
          arabic: true,
          english: true,
          franco: true,
          spanish: false,
        },
        fontSizes: {
          arabic: 4.0,
          english: 3.5,
          franco: 3.0,
          spanish: 3.0,
        },
      },
    ]);
  };

  const removeScreen = (id) => {
    if (externalWindows[id] && !externalWindows[id].closed) {
      externalWindows[id].close();
    }
    setExternalWindows((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setBlackScreens((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    setScreens((prev) => prev.filter((screen) => screen.id !== id));
  };

  const openExternalWindow = (screen) => {
    if (externalWindows[screen.id] && !externalWindows[screen.id].closed) {
      externalWindows[screen.id].close();
    }

    const newWindow = window.open(
      "",
      `screen_${screen.id}`,
      "popup=yes,width=800,height=600"
    );

    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Lyrics Display - Screen ${screen.id}</title>
            <style>
              body {
                margin: 0;
                padding: 0;
                background: #000;
                color: #fff;
                font-family: Arial, sans-serif;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                overflow: hidden;
                width: 100vw;
                height: 100vh;
              }
              .content {
                width: 100vw;
                height: 100vh;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                position: fixed;
                top: 0;
                left: 0;
              }
              .verse {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                text-align: center;
                transition: font-size 0.3s ease;
                padding: 0.5rem;
                box-sizing: border-box;
              }
              .arabic {
                direction: rtl;
                font-family: Arial, sans-serif;
              }
              .english, .franco, .spanish {
                text-align: center;
              }
              .franco {
                font-style: italic;
              }
            </style>
          </head>
          <body>
            <div class="content"></div>
          </body>
        </html>
      `);

      updateExternalWindow(
        newWindow,
        selectedVerse,
        screen.displayOptions,
        screen.fontSizes,
        blackScreens[screen.id]
      );
      newWindow.document.close();

      setExternalWindows((prev) => ({
        ...prev,
        [screen.id]: newWindow,
      }));
    }
  };

  useEffect(() => {
    Object.entries(externalWindows).forEach(([screenId, window]) => {
      if (!window.closed) {
        const screen = screens.find((s) => s.id === parseInt(screenId));
        if (screen) {
          updateExternalWindow(
            window,
            selectedVerse,
            screen.displayOptions,
            screen.fontSizes,
            blackScreens[screenId] || isEndOfSong
          );
        }
      }
    });
  }, [selectedVerse, screens, externalWindows, blackScreens, isEndOfSong]);

  // If no screens are present, render a fallback UI
  if (screens.length === 0) {
    return (
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className={`flex flex-col items-center space-y-4 p-4 pt-0 z-50 ${
          isRTL ? "font-[Tajawal]" : ""
        }`}
        style={isRTL ? { fontFamily: "Tajawal, Amiri, Arial, sans-serif" } : {}}
      >
        <button
          onClick={addScreen}
          className="w-full gap-2 h-10 px-3 rounded-lg flex items-center justify-center bg-black text-white hover:bg-[#0e0e0e] transition-colors"
        >
          <Eye
            size={16}
          />
          <span>{t("controlBar.addScreen")}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`flex flex-col items-start space-y-4 p-4 pt-0 z-50 ${
        isRTL ? "font-[Tajawal]" : ""
      }`}
      style={isRTL ? { fontFamily: "Tajawal, Amiri, Arial, sans-serif" } : {}}
    >
      {screens.map((screen) => (
        <div
          key={screen.id}
          className={`w-full animate-fadeIn ${isRTL ? "text-right" : ""}`}
          style={{
            animation: `${
              screen.id === screens[screens.length - 1]?.id &&
              screens.length > 1
                ? "slideDown 0.3s ease-out"
                : ""
            }`,
          }}
        >
          <div className="w-auto h-14 relative">
            <div
              className={`h-14 bg-black rounded-xl shadow-lg outline outline-1 outline-white/40 backdrop-blur-md flex items-center px-3 justify-between ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              {/* Add button */}
              <button
                onClick={addScreen}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <Plus className="text-white/80" size={16} />
                </div>
              </button>

              {/* Divider */}
              <div
                className={`h-6 ${
                  isRTL ? "border-l" : "border-r"
                } border-white/40 mx-2`}
              />

              {/* Screen label */}
              <div className="text-white/80 text-sm mr-2">
                Screen {screen.id}
              </div>

              {/* Divider */}
              <div
                className={`h-6 ${
                  isRTL ? "border-l" : "border-r"
                } border-white/40 mx-2`}
              />

              {/* Language selector */}
              <div
                className={`flex gap-2 ${
                  isRTL ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                {languages.map((lang) => {
                  const langName = languageFullNames[lang];
                  const isActive = screen.displayOptions[langName];

                  return (
                    <button
                      key={lang}
                      onClick={() => {
                        setScreens((prev) =>
                          prev.map((s) =>
                            s.id === screen.id
                              ? {
                                  ...s,
                                  displayOptions: {
                                    ...s.displayOptions,
                                    [langName]: !s.displayOptions[langName],
                                  },
                                }
                              : s
                          )
                        );
                      }}
                      className={`w-11 h-7 rounded-md flex gap-2 items-center justify-center ${
                        isActive ? "bg-orange-500" : "hover:bg-white/10"
                      } transition-colors`}
                    >
                      <div
                        className={`text-center text-sm font-normal ${
                          isActive ? "text-white" : "text-white/80"
                        } ${lang === "AR" && isRTL ? "font-[Tajawal]" : ""}`}
                      >
                        {lang}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Divider */}
              <div
                className={`h-6 ${
                  isRTL ? "border-l" : "border-r"
                } border-white/40 mx-2`}
              />

              {/* Black Screen */}
              <button
                onClick={() => toggleBlackScreen(screen.id)}
                className={`w-10 h-7 rounded-lg flex items-center justify-center ${
                  blackScreens[screen.id]
                    ? "bg-orange-500"
                    : "hover:bg-white/10"
                } transition-colors`}
              >
                <img
                  src={closeScreen}
                  alt="closeScreenIcon"
                  className="w-full h-full"
                />
              </button>

              {/* Divider */}
              <div
                className={`h-6 ${
                  isRTL ? "border-l" : "border-r"
                } border-white/40 mx-2`}
              />

              {/* Size controls */}
              <div
                className={`flex space-x-1 ${
                  isRTL ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                <button
                  onClick={() => adjustFontSize(screen.id, false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <img
                    src={decrssText}
                    alt="decrssTextIcon"
                    className="w-16 h-16"
                  />
                </button>

                <button
                  onClick={() => adjustFontSize(screen.id, true)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <img
                    src={incressText}
                    alt="incressTextIcon"
                    className="w-16 h-16"
                  />
                </button>

                <button
                  onClick={() => resetFontSizes(screen.id)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <RotateCcw className="text-white/80" size={16} />
                </button>
              </div>

              {/* Divider */}
              <div
                className={`h-6 ${
                  isRTL ? "border-l" : "border-r"
                } border-white/40 mx-2`}
              />

              {/* External Window button */}
              <button
                onClick={() => openExternalWindow(screen)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <Maximize className="text-white/80" size={16} />
              </button>

              {/* Close button */}
              <button
                onClick={() => removeScreen(screen.id)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <X className="text-white/80" size={16} />
              </button>
            </div>
          </div>
        </div>
      ))}

      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap");
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ControlBar;
